import type { ProductSchema as AlgoliaProductType } from '@integration-layer/.nuxt/armani/product-schema'
import { useParamPageItem } from './useParamPageItem'
import { useResetParams } from './useResetParams'
import { type GASetupDatalayerParamPage } from '@integration-layer/schemas/GAEvents/dataLayerSetup'

export const AMPLIENCE_EDITORIAL_PAGE_TYPE = 'editorial-page'
export const AMPLIENCE_SUBHOME_PAGE_TYPE = 'subhome'
export const AMPLIENCE_PLP_PAGE_TYPE = 'category'

/**
 * Generates page-specific parameters for Google Analytics data layer setup.
 *
 * This composable provides contextual information about the current page for analytics tracking,
 * handling different page types such as product pages, Amplience pages, error pages, and generic pages.
 *
 * @param {string} pageType - The type of page being tracked ('productPage', 'ampliencePage', 'errorPage', or 'genericPage')
 * @param {Object} [payload] - Optional payload with additional page-specific details
 * @param {AlgoliaProductType} [payload.item] - Product details for product pages
 * @param {string} [payload.pageType] - Specific page type for Amplience pages
 * @param {number} [payload.pageStatusCode] - HTTP status code for error pages
 * @param {string} [payload.brand] - Brand information
 * @param {string} [payload.gender] - Gender category
 * @param {Array<string>} [payload.categories] - Page categories
 *
 * @returns {void}
 *
 * @example
 * // For a product page
 * useParamPage('productPage', { item: productDetails })
 *
 * // For an Amplience page
 * useParamPage('ampliencePage', { pageType: AMPLIENCE_CATEGORY_PAGE_TYPE, categories: ['clothing'] })
 *
 * // For an error page
 * useParamPage('errorPage', { pageStatusCode: 404 })
 */
export const useParamPage = (
  pageType: 'productPage' | 'ampliencePage' | 'errorPage' | 'genericPage',
  payload?: {
    item?: AlgoliaProductType
    pageType?: string
    pageStatusCode?: number
    brand?: string
    gender?: string
    categories?: Array<string>
    slug?: string
  }
) => {
  const { defaultValues } = useResetParams()
  const { country } = useRouteHelper()
  const formattedBrand = useGAFormattedBrands()

  let paramPage: GASetupDatalayerParamPage = defaultValues.paramPage
  const isProduct = pageType === 'productPage'
  const isAmpliencePage = pageType === 'ampliencePage'
  const isErrorPage = pageType === 'errorPage'
  const isGenericPage = pageType === 'genericPage'

  const pageEntity = useGAEntity<GASetupDatalayerParamPage>('GA_DL_SETUP_PAGE')

  if (isProduct) {
    // absence of payload.item is an error, fallback to default values
    if (payload?.item) {
      paramPage = useParamPageItem(payload.item)
    }
  } else if (isAmpliencePage) {
    paramPage.brand = formattedBrand
    const pageType = mapAmplienceSchemaToGAPageType(payload?.pageType ?? '')
    paramPage.page_type = pageType

    if (pageType === AMPLIENCE_SUBHOME_PAGE_TYPE) {
      const lifestyleBrands = ['armani_casa', 'armani_fiori', 'armani_clubs']

      if (lifestyleBrands.includes(formattedBrand)) {
        paramPage.page_type = 'lifestyle'
      }

      paramPage.sub_page_type =
        payload?.brand === 'armani_beauty' ? payload?.brand : formattedBrand
    }
    if (pageType === AMPLIENCE_PLP_PAGE_TYPE) {
      // paramPage.gender needs to be mapped in italian to 'U', 'D' or 'E'
      const categoryPageGender =
        payload?.gender === 'man'
          ? 'U'
          : payload?.gender === 'woman'
            ? 'D'
            : 'E'

      paramPage.gender = categoryPageGender

      // Set sub_page_type based on country and brand (gender is mapped to M or W)
      const subPageTypeGender =
        payload?.gender === 'man'
          ? 'M'
          : payload?.gender === 'woman'
            ? 'W'
            : 'E' //TODO: check if subPageType non gendered pages should have 'E' or ''
      let subPageTypeCategory = `${country.toUpperCase()}_${mapShortWebsitesAlias(formattedBrand)}_${subPageTypeGender}`

      // Append every category from the array of the payload
      payload?.categories?.forEach(
        category => (subPageTypeCategory = `${subPageTypeCategory}_${category}`)
      )
      paramPage.sub_page_type = subPageTypeCategory
    }
    // Save page slug to the paramPage state for editorial pages.
    // Not pushed to dataLayer, only used as a reference for which page is currently open
    if (pageType === AMPLIENCE_EDITORIAL_PAGE_TYPE) {
      paramPage.page_slug = payload?.slug
    } else {
      paramPage.page_slug = undefined
    }

    // Sub function
  } else if (isGenericPage) {
    // Page is not Amplience-based or error, infer page type from route
    const path = useRoute().path as string
    const pathArray = removeLastEmptyParam(path.split('/')) as string[]
    const { pageType, subPageType } = mapRouteToGAPageType(pathArray)

    paramPage.page_type = pageType
    paramPage.sub_page_type = subPageType
  } else if (isErrorPage) {
    //Overwrite with status code and set 500 in case of unmapped error
    paramPage.page_status_code = payload?.pageStatusCode ?? 500
    paramPage.page_type = payload?.pageStatusCode?.toString() ?? '500'
  }

  pageEntity.value = paramPage
}
